import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Alert, CircularProgress, IconButton } from '@mui/material';
import type { Core } from '@pdftron/webviewer';
import { ChevronLeft, ChevronRight, Maximize, Minus, Plus } from 'lucide-react';
import { AlineButton } from 'src/components/generic/AlineButton';
import { AlineTooltip } from 'src/components/generic/AlineTooltip';
import { initializePdfTron } from 'src/components/viewer/viewer-context';
import { pdfTronKey } from 'src/config';
import { makeStyles } from 'tss-react/mui';

import { SkillHeaderSlot } from './skill-file-header';

/** What the stored file is: a PDF, or a DOCX document copy that Apryse renders as a PDF. */
export type SkillPreviewExtension = 'docx' | 'pdf';

const CONTENT_TYPE_BY_EXTENSION = {
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  pdf: 'application/pdf',
} satisfies Record<SkillPreviewExtension, string>;

// The opening fit-to-width zoom stops here; the zoom controls can still go higher.
const MAX_INITIAL_ZOOM = 1.25;
const MIN_ZOOM = 0.25;
const MAX_ZOOM = 4;
const ZOOM_STEP = 1.2;
const INITIAL_STATE = { failed: false, loaded: false, page: 1, pages: 0, zoom: 1 };

const useStyles = makeStyles()(theme => ({
  controls: { alignItems: 'center', display: 'flex', flexShrink: 0, fontSize: 12, gap: 4 },
  fallback: { inset: 0, overflow: 'auto', position: 'absolute', zIndex: 1 },
  loading: {
    display: 'grid',
    inset: 0,
    placeItems: 'center',
    pointerEvents: 'none',
    position: 'absolute',
  },
  root: { height: 'min(900px, 75vh)', minHeight: 480, position: 'relative' },
  scroll: {
    backgroundColor: theme.appColors.background,
    height: '100%',
    overflow: 'auto',
    position: 'relative',
  },
  viewer: { margin: 'auto' },
}));

/** Reads bundle bytes without joining the document editor's annotations or permissions. */
export const SkillPdfPreview = React.memo(function SkillPdfPreview({
  bytes,
  controlsContainer,
  extension = 'pdf',
  loadingFallback,
}: {
  bytes: Uint8Array<ArrayBuffer>;
  controlsContainer?: HTMLElement | null;
  extension?: SkillPreviewExtension;
  loadingFallback?: React.ReactNode;
}) {
  const { classes } = useStyles();
  const scrollRef = useRef<HTMLDivElement>(null);
  const viewerElementRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<Core.DocumentViewer | null>(null);
  // Fit to width with the zoom capped, until the user zooms or picks Fit width.
  const autoFitRef = useRef(true);
  const positionRef = useRef<{
    autoFit: boolean;
    bytes: Uint8Array<ArrayBuffer>;
    fitWidth: boolean;
    page: number;
    scrollLeft: number;
    scrollTop: number;
    zoom: number;
  } | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState(INITIAL_STATE);
  const retry = useCallback(() => setAttempt(value => value + 1), []);

  useEffect(() => {
    const scroll = scrollRef.current;
    const element = viewerElementRef.current;
    if (!scroll || !element) return;
    let active = true;
    let viewer: Core.DocumentViewer | null = null;
    let observer: ResizeObserver | null = null;
    let loaded = false;
    let rendered = false;
    setState(INITIAL_STATE);
    const fail = () => {
      if (active) setState(current => ({ ...current, failed: true, loaded: false }));
    };
    const rememberPosition = () => {
      if (!active || !loaded || !viewer || !scroll.clientHeight) return;
      positionRef.current = {
        autoFit: autoFitRef.current,
        bytes,
        fitWidth: viewer.getFitMode() === viewer.FitMode.FitWidth,
        page: viewer.getCurrentPage(),
        scrollLeft: scroll.scrollLeft,
        scrollTop: scroll.scrollTop,
        zoom: viewer.getZoomLevel(),
      };
    };
    const update = () => {
      if (!active || !viewer || !loaded) return;
      rememberPosition();
      setState({
        failed: false,
        loaded: rendered,
        page: viewer.getCurrentPage(),
        pages: viewer.getPageCount(),
        zoom: viewer.getZoomLevel(),
      });
    };
    const onLoaded = () => {
      if (!active || !viewer) return;
      loaded = true;
      const previous = positionRef.current;
      if (previous?.bytes === bytes) {
        autoFitRef.current = previous.autoFit;
        if (previous.autoFit) fitWidthCapped(viewer);
        else if (previous.fitWidth) viewer.setFitMode(viewer.FitMode.FitWidth);
        else viewer.zoomTo(previous.zoom);
        viewer.setCurrentPage(Math.min(previous.page, viewer.getPageCount()), false);
        scroll.scrollTop = previous.scrollTop;
        scroll.scrollLeft = previous.scrollLeft;
      } else {
        autoFitRef.current = true;
        fitWidthCapped(viewer);
      }
      update();
    };
    const onPageComplete = (page: number) => {
      if (!active || !loaded || !viewer || page !== viewer.getCurrentPage()) return;
      rendered = true;
      update();
    };
    void initializePdfTron()
      .then(async core => {
        if (!active) return;
        core.disableEmbeddedJavaScript();
        viewer = new core.DocumentViewer();
        viewerRef.current = viewer;
        viewer.setScrollViewElement(scroll);
        viewer.setViewerElement(element);
        viewer.enableAnnotations();
        viewer.getAnnotationManager().enableReadOnlyMode();
        // Core switches between Edit and TextSelect as the pointer moves over a page.
        const editTool = viewer.getTool(core.Tools.ToolNames.EDIT);
        if (editTool instanceof core.Tools.AnnotationEditTool) editTool.enableTextSelection();
        viewer.setToolMode(viewer.getTool(core.Tools.ToolNames.TEXT_SELECT));
        viewer.addEventListener('documentLoaded', onLoaded);
        viewer.addEventListener('pageComplete', onPageComplete);
        viewer.addEventListener('pageNumberUpdated', update);
        viewer.addEventListener('zoomUpdated', update);
        scroll.addEventListener('scroll', rememberPosition);
        observer = new ResizeObserver(() => {
          if (!active || !loaded || !viewer) return;
          if (autoFitRef.current || viewer.getFitMode() === viewer.FitMode.FitWidth) {
            const page = viewer.getCurrentPage();
            if (autoFitRef.current) fitWidthCapped(viewer);
            else viewer.setFitMode(viewer.FitMode.FitWidth);
            viewer.setCurrentPage(page, false);
          }
          viewer.scrollViewUpdated();
        });
        observer.observe(scroll);
        await viewer.loadDocument(
          new Blob([bytes], { type: CONTENT_TYPE_BY_EXTENSION[extension] }),
          {
            extension,
            l: pdfTronKey,
            // Converted to a PDF as the file viewer does, which avoids selection issues in some DOCX files.
            loadAsPDF: true,
            onError: fail,
          }
        );
      })
      .catch(fail);
    return () => {
      active = false;
      observer?.disconnect();
      scroll.removeEventListener('scroll', rememberPosition);
      if (!viewer) return;
      viewer.removeEventListener('documentLoaded', onLoaded);
      viewer.removeEventListener('pageComplete', onPageComplete);
      viewer.removeEventListener('pageNumberUpdated', update);
      viewer.removeEventListener('zoomUpdated', update);
      if (viewerRef.current === viewer) viewerRef.current = null;
      viewer.unmount();
    };
  }, [attempt, bytes, extension]);

  const previousPage = useCallback(() => {
    const viewer = viewerRef.current;
    if (viewer) viewer.setCurrentPage(Math.max(1, viewer.getCurrentPage() - 1), false);
  }, []);
  const nextPage = useCallback(() => {
    const viewer = viewerRef.current;
    if (viewer)
      viewer.setCurrentPage(Math.min(viewer.getPageCount(), viewer.getCurrentPage() + 1), false);
  }, []);
  // A zoom the user picks ends the capped fit-to-width the preview opened with.
  const zoomManually = useCallback((apply: (viewer: Core.DocumentViewer) => void) => {
    const viewer = viewerRef.current;
    if (!viewer) return;
    autoFitRef.current = false;
    apply(viewer);
  }, []);
  const zoomOut = useCallback(
    () =>
      zoomManually(viewer => viewer.zoomTo(Math.max(MIN_ZOOM, viewer.getZoomLevel() / ZOOM_STEP))),
    [zoomManually]
  );
  const zoomIn = useCallback(
    () =>
      zoomManually(viewer => viewer.zoomTo(Math.min(MAX_ZOOM, viewer.getZoomLevel() * ZOOM_STEP))),
    [zoomManually]
  );
  const fitWidth = useCallback(
    () => zoomManually(viewer => viewer.setFitMode(viewer.FitMode.FitWidth)),
    [zoomManually]
  );
  const loading = !state.loaded && !state.failed;
  const controls = (
    <div aria-label="PDF controls" className={classes.controls}>
      <IconButton
        aria-label="Previous PDF page"
        disabled={!state.loaded || state.page <= 1}
        size="small"
        onClick={previousPage}
      >
        <ChevronLeft size={16} />
      </IconButton>
      <span aria-label="PDF page">
        {state.page} / {state.pages || '–'}
      </span>
      <IconButton
        aria-label="Next PDF page"
        disabled={!state.loaded || state.page >= state.pages}
        size="small"
        onClick={nextPage}
      >
        <ChevronRight size={16} />
      </IconButton>
      <IconButton
        aria-label="Zoom out PDF"
        disabled={!state.loaded || state.zoom <= MIN_ZOOM}
        size="small"
        onClick={zoomOut}
      >
        <Minus size={16} />
      </IconButton>
      <span aria-label="PDF zoom">{Math.round(state.zoom * 100)}%</span>
      <IconButton
        aria-label="Zoom in PDF"
        disabled={!state.loaded || state.zoom >= MAX_ZOOM}
        size="small"
        onClick={zoomIn}
      >
        <Plus size={16} />
      </IconButton>
      <AlineTooltip arrow placement="top" title="Fit to width">
        <span>
          <IconButton
            aria-label="Fit to width"
            disabled={!state.loaded}
            size="small"
            onClick={fitWidth}
          >
            <Maximize size={16} />
          </IconButton>
        </span>
      </AlineTooltip>
    </div>
  );
  return (
    <>
      <SkillHeaderSlot container={controlsContainer}>{controls}</SkillHeaderSlot>
      {state.failed && (
        <Alert action={<AlineButton onClick={retry}>Retry</AlineButton>} severity="error">
          Could not load this PDF.
        </Alert>
      )}
      <div
        aria-label={extension === 'pdf' ? 'Attached PDF preview' : 'Document copy preview'}
        className={classes.root}
      >
        <div
          ref={scrollRef}
          aria-hidden={!state.loaded}
          className={classes.scroll}
          style={{ visibility: state.loaded ? 'visible' : 'hidden' }}
        >
          <div ref={viewerElementRef} className={classes.viewer} />
        </div>
        {loading && Boolean(loadingFallback) && (
          <div className={classes.fallback}>{loadingFallback}</div>
        )}
        {loading && !loadingFallback && (
          <div className={classes.loading}>
            <CircularProgress size={24} />
          </div>
        )}
      </div>
    </>
  );
});

function fitWidthCapped(viewer: Core.DocumentViewer) {
  viewer.setFitMode(viewer.FitMode.FitWidth);
  if (viewer.getZoomLevel() > MAX_INITIAL_ZOOM) viewer.zoomTo(MAX_INITIAL_ZOOM);
}
